/* eslint-disable react/prop-types */
import React, { useState, useEffect, memo } from 'react';
import { isDataBrainUrl } from '../../../ChatDSTemplate/utils';
import { useImageCacheLocal } from '../../../../../hooks/useImageCacheLocal';
import './index.scss';
import { useIntl } from 'umi';
import { Icon } from 'tea-component';
import classNames from 'classnames';

interface ISignedImageProps {
  src: string;
  alt?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}

/**
 * 支持 databrain 链接自动签名的图片组件
 * 使用 useLocalState 进行全局图片签名缓存
 */
const SignedImage: React.FC<ISignedImageProps> = memo(({ 
  src, 
  alt, 
  style, 
  ...props 
}) => {
  const [currentSrc, setCurrentSrc] = useState<string>(src);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { getSignedUrl } = useImageCacheLocal();
  const isDataBrainSrc = isDataBrainUrl(src);
  useEffect(() => {
    // 重置状态
    setHasError(false);
    setIsLoading(true);
    
    if (isDataBrainSrc) {
      // 使用 useLocalState 缓存服务获取签名URL
      getSignedUrl(src).then((signedSrc: string) => {
        setCurrentSrc(signedSrc);
        setIsLoading(false);
      }).catch(() => {
        // 签名失败时不设置 currentSrc，保持为空，这样图片就不会显示
        setCurrentSrc('');
        setHasError(true);
        setIsLoading(false);
      });
    } else {
      // 非 databrain URL 直接使用原始 URL
      setCurrentSrc(src);
      setIsLoading(false);
    }
  }, [src]);

  // 如果签名失败，不渲染图片
  if (hasError || !currentSrc) {
    return null;
  }
  // 如果正在加载且没有有效的 src，显示加载状态
  if (isLoading) {
    return (
      <div 
        className='dba-img-signed-box-loading'
        style={style}
        {...props}
      >
        <Icon
          size="l"
          type="loading"
          className="dba-img-signed-box-loading-icon"
        />
        {formatMessage({ id: 'dba_img_loading' })}
      </div>
    );
  }

  return (
    <img 
      src={currentSrc} 
      className={
        classNames('dba-img-signed-box-img', {
          // 最大宽度限制
          'dba-img-signed-box-img-max-width': !isDataBrainSrc,
        })
      }
      alt={alt}
      referrerPolicy="no-referrer"
      {...(src?.includes('databrain') ? { crossOrigin: 'anonymous' } : {})}
      {...props}
      style={{
        ...style,
      }}
    />
  );
});

export default SignedImage;
